import { useCallback, useMemo, useState } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import { updateURLSearchParams } from '@/pages/intelligence/utils/commonUtils';
import { areArraysEqualOrdered } from '@/pages/intelligence/utils/common';
import {
  buildMetricSelectorChange,
  collectAvailableMetricValues,
  ensureWishlistsMetricSelectionByFilterValue,
  ensureValidMetricsParams,
  filterDisabledSourceMetrics,
  type DerivedMetricSelectorState,
} from '@intelligence/pages/TopCharts/PCConsoleGames/utils/metricSelectorHelpers';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiTopChartsBtnLogMes } from '@/pages/intelligence/utils/topChartsBuriedPointCfg';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import type { Dispatch } from 'umi';
import type { PCConsolePageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/type';

interface UsePcConsoleMetricSelectorParams {
  entityType: string;
  pageId: PCConsolePageId;
  cascaderOptions: OptionsItemType[];
  cascaderMap: Record<string, any>;
  metricKeys: string[];
  initialOptions: OptionsItemType[];
  initialValues: string[];
  initialColumns: any[];
  defaultMetacritic: Record<string, string[]>;
  shouldAppendCombineExtras: boolean;
  defaultPageSize: number;
  shouldUpdateMetricsOnFilterChange: boolean;
  isPCConsoleMetricsTable: boolean;
  metricValueRangeTimeRef: MutableRefObject<any>;
  userManuallyChangedRegionRef: MutableRefObject<boolean>;
  hasMetricSelectorChangedRef: MutableRefObject<boolean>;
  filterConfigCur: any;
  filterValue: any;
  setFilterValue: (value: any) => void;
  setFilterPageValue: (value: any) => void;
  setFilterExtend: (value: any) => void;
  setDetailFilterExtend?: (value: any) => void;
  formatMessage: (descriptor: { id: string }) => string | ReactNode;
  dispatch: Dispatch;
  setMetric: (value: string) => void;
  setOperatedGame: (value: boolean) => void;
  getData: (args: {
    params: any;
    isCheckGame: boolean;
    isSearch?: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues?: any;
    myFilterValue?: string;
    metricFilterValue?: string[];
    tableColumns?: any[];
    isNeedLineChartDataValue?: boolean;
    dealParams?: (params: any) => any;
  }) => void;
  getPCConsoleMetricValues: (metrics: any[]) => {
    order_metric?: string;
    order_source?: string;
    source_metrics?: Record<string, string[]>;
  };
}

interface ChangeMetricSelectorArgs {
  payload: string[];
  fetchParams: any;
}

export interface UsePcConsoleMetricSelectorResult {
  metricSelectorOptions: OptionsItemType[];
  metricSelectorValues: string[];
  dataSourcesColumns: any[];
  setDataSourcesColumns: (columns: any[]) => void;
  sortByKey: string;
  setSortByKey: (value: string) => void;
  sortByName: string;
  setSortByName: (value: string) => void;
  metricSelectorAvailableValues: string[];
  updateMetricsAndSetValues: (
    newParams: any,
    shouldUpdateMetrics: boolean,
    filterValueHasChanged: boolean,
    filterOutDisabledValue: boolean,
    metricSelectorValuesLet?: string[],
  ) => any;
  changeMetricSelector: ({ payload, fetchParams }: ChangeMetricSelectorArgs) => Promise<void | undefined>;
}

const usePcConsoleMetricSelector = (
  params: UsePcConsoleMetricSelectorParams,
): UsePcConsoleMetricSelectorResult => {
  const {
    entityType,
    pageId,
    cascaderOptions,
    cascaderMap,
    metricKeys,
    initialOptions,
    initialValues,
    initialColumns,
    defaultMetacritic,
    shouldAppendCombineExtras,
    defaultPageSize,
    shouldUpdateMetricsOnFilterChange,
    isPCConsoleMetricsTable,
    metricValueRangeTimeRef,
    userManuallyChangedRegionRef,
    hasMetricSelectorChangedRef,
    filterConfigCur,
    filterValue,
    setFilterValue,
    setFilterPageValue,
    setFilterExtend,
    setDetailFilterExtend,
    formatMessage,
    dispatch,
    setMetric,
    setOperatedGame,
    getData,
    getPCConsoleMetricValues,
  } = params;

  const [metricSelectorOptions, setMetricSelectorOptions] = useState<OptionsItemType[]>(initialOptions);
  const [metricSelectorValues, setMetricSelectorValues] = useState<string[]>(initialValues);
  const [dataSourcesColumns, setDataSourcesColumns] = useState<any[]>(initialColumns);
  const [sortByName, setSortByName] = useState<string>(initialColumns?.[0]?.text || '');
  const [sortByKey, setSortByKey] = useState<string>(initialColumns?.[0]?.value || '');

  const metricSelectorAvailableValues = useMemo(
    () => collectAvailableMetricValues(metricSelectorOptions),
    [metricSelectorOptions],
  );

  const applyDerivedMetricState = useCallback(
    (derivedState?: DerivedMetricSelectorState<any>) => {
      if (!derivedState) return;
      setDataSourcesColumns(derivedState.metrics as any[]);
      setMetricSelectorOptions(derivedState.metricSelectorOptions);
      setMetricSelectorValues(derivedState.metricSelectorValues);
    },
    [],
  );

  const updateMetricsAndSetValues = useCallback(
    (
      newParams: any,
      shouldUpdateMetrics: boolean,
      filterValueHasChanged: boolean,
      filterOutDisabledValue: boolean,
      metricSelectorValuesLet: string[] = metricSelectorValues,
    ) => {
      const { params: nextParams, derivedState, fallbackSortBy } = ensureValidMetricsParams({
        params: newParams,
        shouldUpdateMetrics,
        filterValueHasChanged,
        filterOutDisabledValue,
        metricSelectorValuesLet,
        pageId,
        cascaderOptions,
        cascaderMap,
        metricKeys,
        getPCConsoleMetricValues,
        shouldAppendCombineExtras,
        defaultMetacritic,
      });

      applyDerivedMetricState(derivedState);

      if (fallbackSortBy) {
        if (fallbackSortBy.key) {
          setSortByKey(fallbackSortBy.key);
        }
        if (fallbackSortBy.name) {
          setSortByName(fallbackSortBy.name);
        }
      }

      return { params: nextParams, updatedMetricValues: derivedState?.metricSelectorValues };
    },
    [
      applyDerivedMetricState,
      cascaderMap,
      cascaderOptions,
      defaultMetacritic,
      getPCConsoleMetricValues,
      metricKeys,
      metricSelectorValues,
      pageId,
      shouldAppendCombineExtras,
    ],
  );

  const applyMetricSelectionSideEffects = useCallback(
    ({
      nextValues,
      metricValues,
      metricsColumns,
      isValidSortMetric,
    }: {
      nextValues: string[];
      metricValues: {
        order_metric: string;
        order_source: string;
        source_metrics: Record<string, string[]>;
      };
      metricsColumns: any[];
      isValidSortMetric?: boolean;
    }) => {
      setMetric(metricValues?.order_metric || (metricsColumns?.[0]?.value as string) || '');

      const sourceMetricsValue = metricValues?.source_metrics || {};
      const metricListValue: string[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const sourceKey in sourceMetricsValue) {
        metricListValue.push(...(sourceMetricsValue[sourceKey] || []));
      }

      const metricValueRangeTimeValue = {
        dateType: metricValueRangeTimeRef?.current?.dateType,
        startDate: metricValueRangeTimeRef?.current?.startDate,
        endDate: metricValueRangeTimeRef?.current?.endDate,
      };

      dispatch({
        type: `${DBI_TOPCHARTS}/setMetricValueListObj`,
        payload: { metricListValue },
      });
      dispatch({
        type: `${DBI_TOPCHARTS}/setMetricValueRangeTime`,
        payload: metricValueRangeTimeValue,
      });

      const newFilterValue = {
        pcConsole_topCharts_metrics: {
          value: nextValues,
        },
        rangeTime: {
          ...filterConfigCur?.defaultFilterValue?.rangeTime,
          type: metricValueRangeTimeRef?.current?.dateType,
          value: [metricValueRangeTimeRef?.current?.startDate, metricValueRangeTimeRef?.current?.endDate],
        },
        metric_selection_for_trends: {
          value: {
            type: 'monthly',
            types: 'all',
            metrics: metricListValue,
            sources: ['all'],
            source_metrics: getSourceMetricsByValue(metricListValue),
            metricsOptions: getMetricOptionByValue(metricListValue),
            selectedMetricsValue: 'metrics',
            shotSelectValue: 'all',
            selectCutVal: 'hot',
          },
        },
      };

      const filterValueCommon = {
        ...filterValue,
        ...newFilterValue,
      };

      setFilterExtend(filterValueCommon);
      setDetailFilterExtend?.(filterValueCommon);
      setFilterValue(filterValueCommon);
      setFilterPageValue(filterValueCommon);

      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
        payload: true,
      });

      dispatch({
        type: `${DBI_TOPCHARTS}/setMetricValue`,
        payload: metricValues?.order_metric || (metricsColumns?.[0]?.value as string) || '',
      });

      setMetricSelectorValues(nextValues);
      setDataSourcesColumns(metricsColumns);

      // 只有当传入的排序参数无效时，才更新 UI 排序状态
      if (!isValidSortMetric) {
        if (Array.isArray(metricsColumns) && metricsColumns.length > 0) {
          setSortByKey(metricsColumns[0]?.value);
          setSortByName(metricsColumns[0]?.text);
        }
      }

      const logList = nextValues?.map((item) => {
        const id = cascaderMap?.[item]?.text || '';
        return String(formatMessage({ id }));
      });
      const metricsSelectorLogKey = `${entityType}_${pageId}_topChartsMetricsSelector`;
      const logConfig = dbiTopChartsBtnLogMes?.[metricsSelectorLogKey];
      logConfig &&
        buriedBtnPoint({
          ...logConfig,
          extInfo: `${logConfig?.extInfo}-${logList?.join(',')}`,
        });
    },
    [
      cascaderMap,
      dispatch,
      entityType,
      filterConfigCur?.defaultFilterValue?.rangeTime,
      filterValue,
      formatMessage,
      pageId,
      setFilterExtend,
      setDetailFilterExtend,
      setFilterPageValue,
      setFilterValue,
      setMetric,
      setSortByKey,
      setSortByName,
    ],
  );

  const changeMetricSelector = useCallback(
    async ({ payload, fetchParams }: ChangeMetricSelectorArgs) => {
      const nextPayload = ensureWishlistsMetricSelectionByFilterValue(payload, filterValue);
      const hasWishlistsMetricAutoRecovered = !areArraysEqualOrdered(nextPayload, payload);
      const { nextValues, metrics: metricsPCConsole, metricValues, fetchParams: preparedFetchParams, isValidSortMetric } =
        buildMetricSelectorChange({
          payload: nextPayload,
          metricSelectorAvailableValues,
          fetchParams,
          isPCConsoleMetricsTable,
          shouldAppendCombineExtras,
          defaultPageSize,
          pageId,
          cascaderOptions,
          cascaderMap,
          getPCConsoleMetricValues,
          metricValueRangeTime: metricValueRangeTimeRef,
          userManuallyChangedRegionRef,
          defaultMetacritic,
        });

      if (areArraysEqualOrdered(nextValues, metricSelectorValues) && !hasWishlistsMetricAutoRecovered) {
        return undefined;
      }

      const { params: newFetchParams } = updateMetricsAndSetValues(
        preparedFetchParams,
        shouldUpdateMetricsOnFilterChange,
        true,
        !hasMetricSelectorChangedRef.current,
        nextValues,
      );

      // 过滤掉被禁用的指标
      const { sourceMetrics, orderSource, orderMetric } = filterDisabledSourceMetrics(
        newFetchParams,
        pageId,
        cascaderOptions,
        nextValues,
        cascaderMap,
        getPCConsoleMetricValues,
      );
      newFetchParams.source_metrics = sourceMetrics;
      newFetchParams.order_source = orderSource;
      newFetchParams.order_metric = orderMetric;

      try {
        getData({
          params: newFetchParams,
          isCheckGame: false,
          isSearch: false,
          isUpdateLineChart: true,
          metricFilterValue: nextValues,
          tableColumns: metricsPCConsole,
        });
      } catch (error) {
        // swallow to keep UX identical to previous implementation
      } finally {
        hasMetricSelectorChangedRef.current = true;
        updateURLSearchParams({ custom_metrics: true });
        setOperatedGame(false);
        applyMetricSelectionSideEffects({
          nextValues,
          metricValues,
          metricsColumns: metricsPCConsole,
          isValidSortMetric,
        });
      }

      return undefined;
    },
    [
      applyMetricSelectionSideEffects,
      cascaderMap,
      defaultMetacritic,
      defaultPageSize,
      getData,
      getPCConsoleMetricValues,
      hasMetricSelectorChangedRef,
      isPCConsoleMetricsTable,
      metricSelectorAvailableValues,
      metricSelectorValues,
      metricValueRangeTimeRef,
      setOperatedGame,
      shouldAppendCombineExtras,
      shouldUpdateMetricsOnFilterChange,
      updateMetricsAndSetValues,
      userManuallyChangedRegionRef,
    ],
  );

  return {
    metricSelectorOptions,
    metricSelectorValues,
    dataSourcesColumns,
    setDataSourcesColumns,
    sortByKey,
    setSortByKey,
    sortByName,
    setSortByName,
    metricSelectorAvailableValues,
    updateMetricsAndSetValues,
    changeMetricSelector,
  };
};

export default usePcConsoleMetricSelector;
